// Для работоспособности service workers в development mode
// import { registerSW } from 'virtual:pwa-register'
// if ('development' === process.env.NODE_ENV) {
//   registerSW({ immediate: true })
// }

/** Сколько ждать controllerchange после SKIP_WAITING, прежде чем всё равно reload. */
const CONTROLLER_CHANGE_TIMEOUT_MS = 3000

export const useServiceWorker = () => {
  const swRegistration = (): Promise<ServiceWorkerRegistration | null> => {
    return new Promise((resolve) => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          resolve(registration)
        })
      } else {
        resolve(null)
      }
    })
  }

  const addServiceWorkerEventListener = (cb: (event: MessageEvent) => void) => {
    if ('serviceWorker' in navigator) {
      const cbWithRemoval = (event: MessageEvent) => {
        cb(event)
        removeServiceWorkerEventListener(cbWithRemoval)
      }
      navigator.serviceWorker.addEventListener('message', cbWithRemoval)
    }
  }

  const removeServiceWorkerEventListener = (cb: (event: MessageEvent) => void) => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', cb)
    }
  }

  const closeServiceWorkerNotification = (sessionId: string) => {
    swRegistration().then((registration) => {
      registration?.getNotifications().then((notificationsList) => {
        notificationsList.forEach((notification) => {
          if (notification.data === sessionId) {
            notification.close()
          }
        })
      })
    })
  }

  /**
   * Сбрасывает весь Cache Storage (precache, runtime assets, NetworkFirst HTML/API и т.д.).
   * Раньше чистился только runtime-кэш `assets` — после «Обновить» оставались старые CSS/JS и HTML.
   */
  const deleteCache = async (): Promise<boolean[]> => {
    if (!('caches' in window)) {
      return []
    }
    const cacheNames = await caches.keys()
    return Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
  }

  /**
   * Активирует ожидающий SW (SKIP_WAITING) и ждёт controllerchange,
   * чтобы следующий reload шёл уже под новым контроллером.
   */
  const activateWaitingServiceWorker = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) {
      return
    }

    const waiting = registration.waiting
    if (!waiting) {
      return
    }

    await new Promise<void>((resolve) => {
      let isSettled = false
      const settle = () => {
        if (isSettled) return
        isSettled = true
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
        window.clearTimeout(timeoutId)
        resolve()
      }

      const onControllerChange = () => {
        settle()
      }

      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
      waiting.postMessage({ type: 'SKIP_WAITING' })

      const timeoutId = window.setTimeout(settle, CONTROLLER_CHANGE_TIMEOUT_MS)
    })
  }

  /** Полная подготовка к обновлению клиента: сброс кэшей + активация нового SW. */
  const prepareAppUpdate = async (): Promise<void> => {
    await deleteCache()
    await activateWaitingServiceWorker()
  }

  return {
    swRegistration,
    addServiceWorkerEventListener,
    closeServiceWorkerNotification,
    deleteCache,
    activateWaitingServiceWorker,
    prepareAppUpdate,
  }
}
